import { env } from '@/app/config/env';
import { apiClient } from '@/shared/services/api/client';
import { unwrapEnvelope } from '@/shared/services/api/envelope';
import type { ApiEnvelope } from '@/shared/services/api/envelope';
import { delay } from '@/shared/utils/asyncUtils';
import { MOCK_LATENCY_MS } from '../constants';
import {
  mockCreateBusinessUnit,
  mockCreateDepartment,
  mockCreateDivision,
  mockCreateEntity,
  mockDeleteBusinessUnit,
  mockDeleteDepartment,
  mockDeleteDivision,
  mockDeleteEntity,
  mockGetBusinessUnit,
  mockGetDepartment,
  mockGetDivision,
  mockGetEntity,
  mockOrganizationTree,
  mockUpdateBusinessUnit,
  mockUpdateDepartment,
  mockUpdateDivision,
  mockUpdateEntity,
} from './mocks/organizationHierarchy.mock';
import type {
  BusinessUnitNodeRecord,
  BusinessUnitRecord,
  BusinessUnitWritePayload,
  DepartmentNodeRecord,
  DepartmentRecord,
  DepartmentWritePayload,
  DivisionNodeRecord,
  DivisionRecord,
  DivisionWritePayload,
  LegalEntityNodeRecord,
  LegalEntityRecord,
  LegalEntityWritePayload,
  OrganizationTreeRecord,
} from '../models';

/**
 * Step 4's API surface: one read of the whole tree, and create/update/delete per level.
 *
 * There is deliberately no bulk hierarchy write (design decision 8). The four CRUD controllers already
 * exist and already validate parentage, so the dialog's Save calls the endpoint for its own level and the
 * step inserts the server's returned node. Save & Next writes nothing.
 *
 * There is also no location function, per decision 16: mapping a location to any level is out of scope and
 * nothing here calls `/locations`.
 *
 * The only mock-aware layer in the module (niraxobid.md section 22) -- each function returns the same shape
 * mocked or real.
 */

const FALLBACK_MESSAGE = 'Server error, please try again';

const ENTITY_PATH = '/legal-entities';
const BUSINESS_UNIT_PATH = '/business-units';
const DIVISION_PATH = '/divisions';
const DEPARTMENT_PATH = '/departments';

const hierarchyApi = {
  getTree: (organizationCode: string) =>
    apiClient.get<ApiEnvelope<OrganizationTreeRecord>>(
      `/organizations/${encodeURIComponent(organizationCode)}/tree`,
    ),

  getEntity: (id: number) => apiClient.get<ApiEnvelope<LegalEntityRecord>>(`${ENTITY_PATH}/${id}`),
  getBusinessUnit: (id: number) =>
    apiClient.get<ApiEnvelope<BusinessUnitRecord>>(`${BUSINESS_UNIT_PATH}/${id}`),
  getDivision: (id: number) => apiClient.get<ApiEnvelope<DivisionRecord>>(`${DIVISION_PATH}/${id}`),
  getDepartment: (id: number) =>
    apiClient.get<ApiEnvelope<DepartmentRecord>>(`${DEPARTMENT_PATH}/${id}`),

  createEntity: (payload: LegalEntityWritePayload) =>
    apiClient.post<ApiEnvelope<LegalEntityNodeRecord>>(ENTITY_PATH, payload),
  updateEntity: (id: number, payload: LegalEntityWritePayload) =>
    apiClient.put<ApiEnvelope<LegalEntityNodeRecord>>(`${ENTITY_PATH}/${id}`, payload),
  deleteEntity: (id: number) => apiClient.delete(`${ENTITY_PATH}/${id}`),

  createBusinessUnit: (payload: BusinessUnitWritePayload) =>
    apiClient.post<ApiEnvelope<BusinessUnitNodeRecord>>(BUSINESS_UNIT_PATH, payload),
  updateBusinessUnit: (id: number, payload: BusinessUnitWritePayload) =>
    apiClient.put<ApiEnvelope<BusinessUnitNodeRecord>>(`${BUSINESS_UNIT_PATH}/${id}`, payload),
  deleteBusinessUnit: (id: number) => apiClient.delete(`${BUSINESS_UNIT_PATH}/${id}`),

  createDivision: (payload: DivisionWritePayload) =>
    apiClient.post<ApiEnvelope<DivisionNodeRecord>>(DIVISION_PATH, payload),
  updateDivision: (id: number, payload: DivisionWritePayload) =>
    apiClient.put<ApiEnvelope<DivisionNodeRecord>>(`${DIVISION_PATH}/${id}`, payload),
  deleteDivision: (id: number) => apiClient.delete(`${DIVISION_PATH}/${id}`),

  createDepartment: (payload: DepartmentWritePayload) =>
    apiClient.post<ApiEnvelope<DepartmentNodeRecord>>(DEPARTMENT_PATH, payload),
  updateDepartment: (id: number, payload: DepartmentWritePayload) =>
    apiClient.put<ApiEnvelope<DepartmentNodeRecord>>(`${DEPARTMENT_PATH}/${id}`, payload),
  deleteDepartment: (id: number) => apiClient.delete(`${DEPARTMENT_PATH}/${id}`),
};

export async function getOrganizationTree(
  organizationCode: string,
): Promise<OrganizationTreeRecord> {
  if (env.ENABLE_MOCKS) {
    await delay(MOCK_LATENCY_MS);
    return mockOrganizationTree(organizationCode);
  }
  return unwrapEnvelope(await hierarchyApi.getTree(organizationCode), FALLBACK_MESSAGE);
}

/* -- reading one row, for the edit dialog ---------------------------------- */

/*
  The tree carries only an id, a name and a code per row, and every level's PUT
  is a total replacement -- so the edit dialog has to read the full row first or
  it would write back blanks over everything the tree omits.
*/

export async function getEntity(id: number): Promise<LegalEntityRecord> {
  if (env.ENABLE_MOCKS) {
    await delay(MOCK_LATENCY_MS);
    return mockGetEntity(id);
  }
  return unwrapEnvelope(await hierarchyApi.getEntity(id), FALLBACK_MESSAGE);
}

export async function getBusinessUnit(id: number): Promise<BusinessUnitRecord> {
  if (env.ENABLE_MOCKS) {
    await delay(MOCK_LATENCY_MS);
    return mockGetBusinessUnit(id);
  }
  return unwrapEnvelope(await hierarchyApi.getBusinessUnit(id), FALLBACK_MESSAGE);
}

export async function getDivision(id: number): Promise<DivisionRecord> {
  if (env.ENABLE_MOCKS) {
    await delay(MOCK_LATENCY_MS);
    return mockGetDivision(id);
  }
  return unwrapEnvelope(await hierarchyApi.getDivision(id), FALLBACK_MESSAGE);
}

export async function getDepartment(id: number): Promise<DepartmentRecord> {
  if (env.ENABLE_MOCKS) {
    await delay(MOCK_LATENCY_MS);
    return mockGetDepartment(id);
  }
  return unwrapEnvelope(await hierarchyApi.getDepartment(id), FALLBACK_MESSAGE);
}

/* -- entity ---------------------------------------------------------------- */

export async function createEntity(
  payload: LegalEntityWritePayload,
): Promise<LegalEntityNodeRecord> {
  if (env.ENABLE_MOCKS) {
    await delay(MOCK_LATENCY_MS);
    return mockCreateEntity(payload);
  }
  return unwrapEnvelope(await hierarchyApi.createEntity(payload), FALLBACK_MESSAGE);
}

export async function updateEntity(
  id: number,
  payload: LegalEntityWritePayload,
): Promise<LegalEntityNodeRecord> {
  if (env.ENABLE_MOCKS) {
    await delay(MOCK_LATENCY_MS);
    return mockUpdateEntity(id, payload);
  }
  return unwrapEnvelope(await hierarchyApi.updateEntity(id, payload), FALLBACK_MESSAGE);
}

export async function deleteEntity(id: number): Promise<void> {
  if (env.ENABLE_MOCKS) {
    await delay(MOCK_LATENCY_MS);
    mockDeleteEntity(id);
    return;
  }
  await hierarchyApi.deleteEntity(id);
}

/* -- business unit --------------------------------------------------------- */

export async function createBusinessUnit(
  payload: BusinessUnitWritePayload,
): Promise<BusinessUnitNodeRecord> {
  if (env.ENABLE_MOCKS) {
    await delay(MOCK_LATENCY_MS);
    return mockCreateBusinessUnit(payload);
  }
  return unwrapEnvelope(await hierarchyApi.createBusinessUnit(payload), FALLBACK_MESSAGE);
}

export async function updateBusinessUnit(
  id: number,
  payload: BusinessUnitWritePayload,
): Promise<BusinessUnitNodeRecord> {
  if (env.ENABLE_MOCKS) {
    await delay(MOCK_LATENCY_MS);
    return mockUpdateBusinessUnit(id, payload);
  }
  return unwrapEnvelope(await hierarchyApi.updateBusinessUnit(id, payload), FALLBACK_MESSAGE);
}

export async function deleteBusinessUnit(id: number): Promise<void> {
  if (env.ENABLE_MOCKS) {
    await delay(MOCK_LATENCY_MS);
    mockDeleteBusinessUnit(id);
    return;
  }
  await hierarchyApi.deleteBusinessUnit(id);
}

/* -- division -------------------------------------------------------------- */

export async function createDivision(
  payload: DivisionWritePayload,
): Promise<DivisionNodeRecord> {
  if (env.ENABLE_MOCKS) {
    await delay(MOCK_LATENCY_MS);
    return mockCreateDivision(payload);
  }
  return unwrapEnvelope(await hierarchyApi.createDivision(payload), FALLBACK_MESSAGE);
}

export async function updateDivision(
  id: number,
  payload: DivisionWritePayload,
): Promise<DivisionNodeRecord> {
  if (env.ENABLE_MOCKS) {
    await delay(MOCK_LATENCY_MS);
    return mockUpdateDivision(id, payload);
  }
  return unwrapEnvelope(await hierarchyApi.updateDivision(id, payload), FALLBACK_MESSAGE);
}

export async function deleteDivision(id: number): Promise<void> {
  if (env.ENABLE_MOCKS) {
    await delay(MOCK_LATENCY_MS);
    mockDeleteDivision(id);
    return;
  }
  await hierarchyApi.deleteDivision(id);
}

/* -- department ------------------------------------------------------------ */

export async function createDepartment(
  payload: DepartmentWritePayload,
): Promise<DepartmentNodeRecord> {
  if (env.ENABLE_MOCKS) {
    await delay(MOCK_LATENCY_MS);
    return mockCreateDepartment(payload);
  }
  return unwrapEnvelope(await hierarchyApi.createDepartment(payload), FALLBACK_MESSAGE);
}

export async function updateDepartment(
  id: number,
  payload: DepartmentWritePayload,
): Promise<DepartmentNodeRecord> {
  if (env.ENABLE_MOCKS) {
    await delay(MOCK_LATENCY_MS);
    return mockUpdateDepartment(id, payload);
  }
  return unwrapEnvelope(await hierarchyApi.updateDepartment(id, payload), FALLBACK_MESSAGE);
}

export async function deleteDepartment(id: number): Promise<void> {
  if (env.ENABLE_MOCKS) {
    await delay(MOCK_LATENCY_MS);
    mockDeleteDepartment(id);
    return;
  }
  await hierarchyApi.deleteDepartment(id);
}
